import React, { useState, useCallback, useRef } from 'react';
import { prefillTrait } from '@/lib/wizard/appearance';
import { useSiteFlags } from '@/context/SiteFlagsContext';
import { GUARANTEE_SHORT } from '@/lib/seo/site-facts';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Camera, Info, Check, Users, Sparkles, Loader2 } from 'lucide-react';
import { SimplePhotoUpload } from '@/components/create-book/SimplePhotoUpload';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FaceDetectionWizard, FaceDetectionWizardRef } from '../FaceDetectionWizard';
import { UseFormReturn } from 'react-hook-form';
import { CreateBookFormData } from '../../types';
import { useDropzone } from 'react-dropzone';
import Image from 'next/image';
import { useWakeLock } from '@/utils/wakeLock';
import { PrivacyBadge } from '@/components/common/PrivacyBadge';
import { PhotoConsentCheckbox } from '@/components/consent/PhotoConsentCheckbox';
import { photoConsentJson } from '@/components/consent/photo-consent-gate';
import { PHOTO_CONSENT_VERSION } from '@/lib/ethics/photo-consent';

interface PhotoUploadStepProps {
  photoUrl?: string;
  onPhotoChange: (url: string) => void;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onUploadError?: (error: string) => void;
  storyType?: 'single' | 'multi';
  form?: UseFormReturn<CreateBookFormData>;
  onFacesDetected?: (count: number) => void;
  childName?: string;
  childAge?: number;
  onNameChange?: (name: string) => void;
  onAgeChange?: (age: number) => void;
  onAnalysisComplete?: (analysis: any) => void;
  /** The consent tick (R3, unticked by default), held by the wizard so the book request sends it too. */
  photoConsent?: string | null;
  onPhotoConsentChange?: (version: string | null) => void;
  /** The photo is the free-cover one (?fromCover=): no tick asked, the server inherits the cover's consent (R5). */
  consentNotNeeded?: boolean;
  /** The free-cover slug sent with the seed photo's detect/analyze requests, so the server inherits its consent. */
  coverSlug?: string | null;
}

// Client-safe helper functions (no server dependencies)
// Note: Age is now stored as exact number (string), not ranges

const mapGenderToOption = (gender: string, genderProbabilities?: any): string => {
  const normalized = gender.toLowerCase();
  if (normalized === 'male' || normalized === 'boy') return 'boy';
  if (normalized === 'female' || normalized === 'girl') return 'girl';
  
  // Never return 'neutral' - use probabilities or default to 'boy'
  if (genderProbabilities) {
    const male = genderProbabilities.male || 0;
    const female = genderProbabilities.female || 0;
    return male >= female ? 'boy' : 'girl';
  }
  
  // Default to 'boy' if unsure
  return 'boy';
};

export function PhotoUploadStep({
  photoUrl,
  onPhotoChange,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  storyType = 'single',
  form,
  onFacesDetected,
  childName,
  childAge,
  onNameChange,
  onAgeChange,
  onAnalysisComplete,
  photoConsent = null,
  onPhotoConsentChange,
  consentNotNeeded = false,
  coverSlug = null
}: PhotoUploadStepProps) {
  const { redoGuarantee } = useSiteFlags();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showFaceDetection, setShowFaceDetection] = useState(false);
  const [detectedFacesCount, setDetectedFacesCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const faceDetectionRef = useRef<FaceDetectionWizardRef>(null);
  
  // Wake lock hook
  const { requestWakeLock, releaseWakeLock } = useWakeLock();

  // Use refs to avoid stale closures in the globally registered function
  const photoUrlRef = useRef(photoUrl);
  const formRef = useRef(form);
  // The tick at call time (the analysis is triggered from the wizard through window.__triggerAIAnalysis)
  const photoConsentRef = useRef(photoConsent);
  photoConsentRef.current = photoConsent;
  const coverSlugRef = useRef(coverSlug);
  coverSlugRef.current = coverSlug;
  // A photo was picked before the tick (held by the uploader until it is ticked)
  const [consentBlocked, setConsentBlocked] = useState(false);

  // Keep refs updated
  React.useEffect(() => {
    photoUrlRef.current = photoUrl;
  }, [photoUrl]);

  React.useEffect(() => {
    formRef.current = form;
  }, [form]);

  // Separate function for AI analysis with AGGRESSIVE retry logic
  // This function MUST succeed for the wizard to proceed - we retry all errors
  const triggerAIAnalysis = async (faces: any[], retryCount = 0): Promise<any[] | undefined> => {
    // Use refs to get current values (avoids stale closure)
    const currentPhotoUrl = photoUrlRef.current;
    const currentForm = formRef.current;

    if (!currentPhotoUrl || !currentForm) {
      console.error('[WIZARD] ❌ triggerAIAnalysis called but photoUrl or form is missing!', {
        hasPhotoUrl: !!currentPhotoUrl,
        hasForm: !!currentForm
      });
      return undefined;
    }

    // Aggressive retry: 4 attempts with exponential backoff (2s, 4s, 8s)
    const maxRetries = 3;

    // Only set analyzing state on first attempt
    if (retryCount === 0) {
      setIsAnalyzing(true);
      // Request wake lock to prevent screen from sleeping during analysis
      await requestWakeLock('AI photo analysis');
    }

    try {
      console.log(`[WIZARD] 🔄 AI analysis attempt ${retryCount + 1}/${maxRetries + 1} for ${faces.length} faces`);

      // Create AbortController with 90-second timeout for OpenAI analysis
      // Very generous timeout to handle slow API responses
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn('[WIZARD] ⏱️ AI analysis timeout after 90s - aborting request');
        controller.abort('timeout');
      }, 90000);

      const response = await fetch('/api/analyze-multi-character-photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photoUrl: currentPhotoUrl,
          detectedFaces: faces.map((face, idx) => ({
            index: idx,
            bbox: face.expanded_bbox || face.bbox
          })),
          ...photoConsentJson(photoConsentRef.current),
          ...(!photoConsentRef.current && coverSlugRef.current ? { fromCoverSlug: coverSlugRef.current } : {})
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const analysis = await response.json();
        console.log('[WIZARD] ✅ AI analysis complete:', analysis);

        // Get current characters to preserve names
        const currentCharacters = currentForm.getValues('characters') || [];

        // Update characters with AI-detected traits while preserving names
        const updatedCharacters = currentCharacters.map((char, idx) => {
          const aiData = analysis[`person_${idx}`];
          if (aiData) {
            return {
              ...char,
              name: char.name, // Preserve the name!
              age: String(aiData.estimatedAge), // Store exact age as string
              gender: mapGenderToOption(aiData.gender, aiData.genderProbabilities),
              hairColor: prefillTrait('hairColor', aiData.hairColor, aiData.fieldConfidence),
              skinTone: prefillTrait('skinTone', aiData.skinTone, aiData.fieldConfidence),
              eyeColor: prefillTrait('eyeColor', aiData.eyeColor, aiData.fieldConfidence),
              hairTexture: prefillTrait('hairTexture', aiData.hairTexture, aiData.fieldConfidence),
              // Store confidence and notes for UI feedback
              aiConfidence: aiData.confidence || 0,
              aiFieldConfidence: aiData.fieldConfidence,
              aiNotes: aiData.additionalNotes || ''
            };
          }
          return char;
        });

        currentForm.setValue('characters', updatedCharacters);
        console.log('[WIZARD] ✅ Updated characters with AI data:', updatedCharacters);

        // Clean up on success
        setIsAnalyzing(false);
        await releaseWakeLock();

        return updatedCharacters; // Return the results
      } else {
        const errorText = await response.text();
        console.error('[WIZARD] ❌ AI analysis API error:', response.status, errorText);
        throw new Error(`API error ${response.status}: ${errorText}`);
      }
    } catch (error: any) {
      console.error(`[WIZARD] ❌ AI analysis error (attempt ${retryCount + 1}/${maxRetries + 1}):`, error.name, error.message);

      // RETRY ALL ERRORS - we must succeed for good UX
      if (retryCount < maxRetries) {
        // Exponential backoff: 2s, 4s, 8s
        const delayMs = 2000 * Math.pow(2, retryCount);
        console.log(`[WIZARD] 🔄 Retrying AI analysis in ${delayMs/1000}s... (attempt ${retryCount + 2}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return triggerAIAnalysis(faces, retryCount + 1);
      }

      // All retries exhausted - clean up and return undefined
      console.error('[WIZARD] ❌ AI analysis FAILED after all retries - this should trigger an error in the wizard');
      setIsAnalyzing(false);
      await releaseWakeLock();
      return undefined;
    }
  };

  // Store the ref and handlers globally for the wizard to access
  // This must be AFTER the function definitions
  React.useEffect(() => {
    if (storyType === 'multi') {
      (window as any).__faceDetectionRef = faceDetectionRef;
      (window as any).__triggerAIAnalysis = triggerAIAnalysis;
      console.log('[WIZARD] 📌 Registered __triggerAIAnalysis on window');
    }
    return () => {
      delete (window as any).__faceDetectionRef;
      delete (window as any).__triggerAIAnalysis;
    };
  }, [storyType]); // Only depends on storyType - function uses refs for other values

  // For single character flow, show AI-powered upload
  if (storyType === 'single') {
    const onDrop = useCallback(
      async (acceptedFiles: File[]) => {
        console.log('[PhotoUploadStep] onDrop triggered with files:', acceptedFiles.length);
        setError(null);
        setIsUploading(true);
        
        // For single character, explicitly enforce only 1 file
        if (acceptedFiles.length > 1) {
          setError('Please select only one photo for a single character story');
          setIsUploading(false);
          return;
        }
        
        // Validate file types
        const validFiles = acceptedFiles.filter(
          file => file.type === 'image/jpeg' || file.type === 'image/png'
        );
        
        if (validFiles.length !== acceptedFiles.length) {
          setError('Only JPG and PNG images are accepted');
          setIsUploading(false);
          return;
        }
        
        // For single character, limit to 1 photo
        const file = validFiles[0];
        
        try {
          // Upload file
          const formData = new FormData();
          formData.append('file', file);
          
          console.log('[PhotoUploadStep] Uploading file:', file.name, 'Size:', file.size);
          
          const response = await fetch('/api/supabase-upload?bucket=child-photos&folder=childPhotoUploader', {
            method: 'POST',
            body: formData
          });
          
          console.log('[PhotoUploadStep] Upload response status:', response.status);
          
          if (!response.ok) {
            throw new Error('Upload failed');
          }
          
          const data = await response.json();
          const serverUrl = data.url;
          
          // Update state with server URL
          onPhotoChange(serverUrl);
          if (onUploadComplete) onUploadComplete();
          
          // Analyze the photo automatically
          await analyzePhoto(serverUrl);
        } catch (error) {
          console.error('Error uploading photo:', error);
          setError('Failed to upload photo. Please try again.');
          if (onUploadError) onUploadError('Upload failed');
        } finally {
          setIsUploading(false);
        }
      },
      [onPhotoChange, onUploadComplete, onUploadError]
    );

    const analyzePhoto = async (photoUrl: string) => {
      setIsAnalyzing(true);
      
      // Request wake lock to prevent screen from sleeping during analysis
      await requestWakeLock('Single character photo analysis');
      
      try {
        // Call the API endpoint
        const response = await fetch('/api/analyze-photo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ photoUrl })
        });
        
        if (!response.ok) {
          throw new Error('Failed to analyze photo');
        }
        
        const analysis = await response.json();
        console.log('[PhotoUploadStep] AI Analysis result:', analysis);
        setAnalysisResult(analysis);
        
        // Map hex colors back to color names for the form
        const hexToColorName = (hex: string): string => {
          const colorMap: Record<string, string> = {
            '#000000': 'black',
            '#4a2c2a': 'brown',
            '#e6be8a': 'blonde',
            '#8d4a43': 'red',
            '#922724': 'auburn',
            '#d0d0d0': 'gray',
            '#ffffff': 'white',
            '#ffe0bd': 'light',
            '#e5c298': 'medium',
            '#c68642': 'olive',
            '#8d5524': 'brown',
            '#593d26': 'dark'
          };
          
          return colorMap[hex] || hex; // Return hex if no mapping found
        };
        
        // Notify parent component with properly mapped data
        if (onAnalysisComplete && analysis) {
          // Never use "OTHER" for gender - if AI can't decide, use the one with higher probability
          let gender = analysis.gender;
          if (!gender || gender.toUpperCase() === 'OTHER' || gender === 'unknown') {
            // If we have gender probabilities, use the highest one
            if (analysis.genderProbabilities) {
              const male = analysis.genderProbabilities.male || 0;
              const female = analysis.genderProbabilities.female || 0;
              gender = male >= female ? 'MALE' : 'FEMALE';
            } else {
              // Default to MALE if no other info available
              gender = 'MALE';
            }
            console.log('[PhotoUploadStep] Gender was OTHER/unknown, defaulting to:', gender);
          }
          
          const mappedAnalysis = {
            gender: gender.toUpperCase(),
            hairColor: hexToColorName(analysis.hairColor) || 'black',
            hairStyle: analysis.hairStyle || 'short', 
            skinColor: hexToColorName(analysis.skinColor) || 'medium',
            skinTone: hexToColorName(analysis.skinColor) || 'medium', // Also set skinTone
            eyeColor: analysis.eyeColor || 'brown',
            hairTexture: analysis.hairTexture || 'straight',
            confidence: analysis.confidence || 0.5
          };
          console.log('[PhotoUploadStep] Mapped analysis:', mappedAnalysis);
          onAnalysisComplete(mappedAnalysis);
        }
        
        // Auto-select age if detected
        if (analysis.estimatedAge && onAgeChange) {
          const age = Math.round(analysis.estimatedAge);
          const clampedAge = Math.min(Math.max(age, 1), 13); // 13 represents 13+
          console.log('[PhotoUploadStep] Auto-selecting age:', clampedAge, 'from estimated:', analysis.estimatedAge);
          onAgeChange(clampedAge);
        }
      } catch (error) {
        console.error('Error analyzing photo:', error);
        // Don't show error to user, just proceed without AI assistance
      } finally {
        setIsAnalyzing(false);
        // Release wake lock when done
        await releaseWakeLock();
      }
    };

    const removePhoto = () => {
      console.log('[PhotoUploadStep] Removing photo');
      onPhotoChange('');
      setAnalysisResult(null);
      // Clear age selection when photo is removed
      if (form) {
        form.setValue('childAge', '');
      } else if (onAgeChange) {
        onAgeChange(0);
      }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
      },
      maxSize: 5 * 1024 * 1024, // 5MB
      maxFiles: 1,
      multiple: false, // Explicitly set multiple to false
      onDropAccepted: (files) => console.log('[PhotoUploadStep] Files accepted:', files.length),
      onDropRejected: (rejections) => {
        console.log('[PhotoUploadStep] Files rejected:', rejections);
        if (rejections.some(r => r.errors.some(e => e.code === 'too-many-files'))) {
          setError('Please select only one photo');
        }
      },
      onError: (err) => console.error('[PhotoUploadStep] Dropzone error:', err)
    });

    return (
      <div className="space-y-6">
        {/* Privacy Badge for single character */}
        <PrivacyBadge variant="compact" />
        
        {/* Upload area - moved to top */}
        {!photoUrl ? (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragActive ? 'border-purple-500 bg-purple-50' : 'border-purple-200 hover:border-purple-400'
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Camera className="text-purple-600" size={32} />
              </div>
              <h3 className="text-lg font-medium text-purple-800 mb-2">
                {isDragActive ? 'Drop the photo here...' : 'Upload a photo'}
              </h3>
              <p className="text-gray-600 mb-2">
                Upload one clear photo of your child
              </p>
              <p className="text-sm text-gray-500 mb-4">
                We'll analyze the photo to personalize the story character
              </p>
              <button className="bg-purple-600 text-white px-6 py-2 rounded-full hover:bg-purple-700 transition shadow-sm">
                Browse Files
              </button>
              <p className="text-xs text-gray-500 mt-4">
                JPEG or PNG • Max 5MB • Single photo only
              </p>
            </div>
          </div>
        ) : (
          <div className="relative rounded-lg overflow-hidden h-64 shadow-md bg-gray-50">
            <Image
              src={photoUrl}
              alt="Uploaded photo"
              fill
              style={{ objectFit: 'contain' }}
            />
            <button
              type="button"
              onClick={removePhoto}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-md"
              aria-label="Remove photo"
            >
              <X size={18} />
            </button>
            {isAnalyzing && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="text-white text-center">
                  <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                  <p className="text-sm">Analyzing photo...</p>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg">
            {error}
          </div>
        )}

        {/* AI Analysis indicator */}
        {analysisResult && analysisResult.confidence > 0.5 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-purple-50 p-4 rounded-lg"
          >
            <p className="text-sm text-purple-800 flex items-center">
              <Sparkles className="mr-2" size={16} />
              <strong>Analysis Complete!</strong> We've analyzed the photo to help personalize your story.
            </p>
            {analysisResult.confidence < 0.7 && (
              <p className="text-xs text-purple-600 mt-1">
                You can adjust appearance details in the next step if needed.
              </p>
            )}
          </motion.div>
        )}

        {/* Name and Age fields - moved to bottom */}
        <div className="space-y-4">
          <div>
            <label htmlFor="childFirstName" className="block text-sm font-medium text-gray-700 mb-1">
              Child's First Name
            </label>
            {form ? (
              <input
                id="childFirstName"
                type="text"
                {...form.register('childFirstName')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter your child's name"
              />
            ) : (
              <input
                id="childFirstName"
                type="text"
                value={childName || ''}
                onChange={(e) => onNameChange?.(e.target.value.trim())}
                onBlur={(e) => onNameChange?.(e.target.value.trim())}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter your child's name"
                required
              />
            )}
            {form && form.formState.errors.childFirstName && (
              <p className="text-red-500 text-sm mt-1">{form.formState.errors.childFirstName.message || 'Child\'s name is required'}</p>
            )}
            {!form && !childName && (
              <p className="text-red-500 text-sm mt-1">Child's name is required</p>
            )}
          </div>
          
          <div>
            <label htmlFor="childAge" className="block text-sm font-medium text-gray-700 mb-1">
              Age
            </label>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((age) => (
                <button
                  key={age}
                  type="button"
                  onClick={() => {
                    if (form) {
                      form.setValue('childAge', age.toString());
                    } else {
                      onAgeChange?.(age);
                    }
                  }}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    (form ? form.watch('childAge') === age.toString() : childAge === age)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {age}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  if (form) {
                    form.setValue('childAge', '13');
                  } else {
                    onAgeChange?.(13);
                  }
                }}
                className={`px-4 py-2 rounded-full font-medium transition-all ${
                  (form ? form.watch('childAge') === '13' : childAge === 13)
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                13+
              </button>
            </div>
            {form && form.formState.errors.childAge && (
              <p className="text-red-500 text-sm mt-1">{form.formState.errors.childAge.message || 'Please select your child\'s age'}</p>
            )}
            {!form && !childAge && (
              <p className="text-red-500 text-sm mt-1">Please select your child's age</p>
            )}
          </div>
        </div>

        {/* Simplified disclaimer */}
        <div className="flex justify-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowDisclaimer(!showDisclaimer)}
            className="text-gray-500 hover:text-gray-700"
          >
            <Info className="w-4 h-4 mr-1" />
            Photo likeness info
          </Button>
        </div>
        
        <AnimatePresence>
          {showDisclaimer && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                <h4 className="font-medium text-amber-800 mb-2">
                  About Photo Likeness
                </h4>
                <ul className="space-y-1 text-amber-700">
                  <li>• We create our best interpretation</li>
                  <li>• Results vary by photo quality</li>
                  <li>• Better results with clear, well-lit photos</li>
                  <li>• Preview before purchasing</li>
                  {redoGuarantee && <li>• {GUARANTEE_SHORT}</li>}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {isUploading && (
          <div className="flex items-center justify-center text-gray-600">
            <Loader2 className="animate-spin mr-2" size={20} />
            Uploading photo...
          </div>
        )}
      </div>
    );
  }

  // Multi-character flow remains unchanged
  const handlePhotoUpload = (url: string) => {
    onPhotoChange(url);

    // For multi-character stories, show face detection
    if (storyType === 'multi' && url) {
      setShowFaceDetection(true);
    }
  };

  // A photo can arrive already attached before the user ever touches
  // SimplePhotoUpload — e.g. seeded from the free-cover funnel via
  // form.setValue('characterPhoto', ...) on the wizard's mount effect. That
  // bypasses handlePhotoUpload above, so showFaceDetection would otherwise
  // never flip true and FaceDetectionWizard would never mount, permanently
  // blocking Next (multi requires selected faces). Detect that case and
  // drive it through the exact same handlePhotoUpload path a real upload
  // uses, so the seeded photo behaves identically to a fresh one.
  // Guarded on detectedFaces being empty so this never re-fires once face
  // selection has actually completed (e.g. navigating back to this step).
  const autoTriggeredSeedRef = useRef(false);
  React.useEffect(() => {
    if (storyType !== 'multi') return;
    if (autoTriggeredSeedRef.current) return;
    if (!photoUrl) return;
    if (showFaceDetection) return; // already in progress via a real upload
    const existingFaces = form?.getValues('detectedFaces') || [];
    if (existingFaces.length > 0) return; // already went through detection
    // A free cover without stored consent: its photo waits for the tick before it goes to face detection (R5)
    if (!consentNotNeeded && !photoConsent) return;
    autoTriggeredSeedRef.current = true;
    handlePhotoUpload(photoUrl);
  }, [photoUrl, storyType, photoConsent, consentNotNeeded]);

  const handleFacesConfirmed = async (faces: any[], mainCharacterIndex: number) => {
    console.log("[PhotoUploadStep] handleFacesConfirmed called with", faces.length, "faces");
    console.log('[WIZARD] Faces confirmed:', faces.length, 'main character index:', mainCharacterIndex);
    
    // Store face data in form
    if (form) {
      form.setValue('detectedFaces', faces);
      form.setValue('characterCount', faces.length);
      form.setValue('mainCharacterIndex', mainCharacterIndex); // Set main character index right away
      
      // Initialize characters array with detected faces (keep names from face detection)
      const newCharacters = faces.map((face, index) => ({
        name: face.name || '',
        age: '',
        gender: '',
        skinTone: '',
        hairColor: '',
        eyeColor: '',
        hairTexture: '',
        bbox: face.expanded_bbox || face.bbox,
        thumbnail: face.thumbnail || ''
      }));
      form.setValue('characters', newCharacters);
      console.log('[WIZARD] Initialized characters array with', newCharacters.length, 'characters');
    }
    
    setDetectedFacesCount(faces.length);
    setShowFaceDetection(false);
    console.log("[PhotoUploadStep] Face detection complete, hiding UI");
    
    // Notify parent
    if (onFacesDetected) {
      onFacesDetected(faces.length);
    }
  };

  const handleManualMode = () => {
    console.log('[WIZARD] Switching to manual face selection');
    setShowFaceDetection(false);
    console.log("[PhotoUploadStep] Face detection complete, hiding UI");
    
    // Default to 2 characters for manual mode
    if (form) {
      form.setValue('characterCount', 2);
      
      // Initialize characters array for manual mode
      const newCharacters = Array(2).fill(null).map(() => ({
        name: '',
        age: '',
        gender: '',
        skinTone: '',
        hairColor: '',
        eyeColor: '',
        hairTexture: ''
      }));
      form.setValue('characters', newCharacters);
      console.log('[WIZARD] Initialized characters array for manual mode with 2 characters');
    }
    if (onFacesDetected) {
      onFacesDetected(2);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Privacy Badge for multi-character */}
      <PrivacyBadge variant="compact" />
      
      {/* Main upload area */}
      {!showFaceDetection ? (
        <div className="relative">
          {!consentNotNeeded && (
            <div className="mb-4 space-y-2">
              <PhotoConsentCheckbox
                checked={!!photoConsent}
                onChange={(checked) => {
                  if (checked) setConsentBlocked(false);
                  onPhotoConsentChange?.(checked ? PHOTO_CONSENT_VERSION : null);
                }}
                variant="book"
                error={consentBlocked && !photoConsent}
              />
              {photoUrl && !photoConsent && (
                <p className="text-sm text-gray-600" role="status">Tick the box to use this photo.</p>
              )}
            </div>
          )}
          <SimplePhotoUpload
            photoUrl={photoUrl}
            onPhotoChange={handlePhotoUpload}
            onUploadStart={onUploadStart}
            onUploadComplete={onUploadComplete}
            onUploadError={onUploadError}
            photoConsent={photoConsent}
            requireConsent={!consentNotNeeded}
            onConsentRequired={() => setConsentBlocked(true)}
          />
          
          {/* Upload progress indicator */}
          {!photoUrl && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 text-center"
            >
              <p className="text-sm text-gray-600">
                {storyType === 'multi' ? (
                  <>
                    <Users className="inline w-4 h-4 mr-1" />
                    Upload a group photo with 1-5 people
                  </>
                ) : (
                  <>
                    <Camera className="inline w-4 h-4 mr-1" />
                    Take a clear, well-lit photo
                  </>
                )}
              </p>
            </motion.div>
          )}
          
          {/* Success state */}
          {photoUrl && !showFaceDetection && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mt-4 flex items-center justify-center text-green-600"
            >
              <Check className="w-5 h-5 mr-2" />
              <span className="font-medium">
                {storyType === 'multi' && detectedFacesCount > 0
                  ? `${detectedFacesCount} ${detectedFacesCount === 1 ? 'person' : 'people'} selected!`
                  : 'Photo uploaded successfully!'}
              </span>
            </motion.div>
          )}
          
          {/* AI Analysis loading indicator for multi-character */}
          {storyType === 'multi' && isAnalyzing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 flex items-center justify-center"
            >
              <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-3">
                <Loader2 className="animate-spin w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Analyzing characters...</p>
                  <p className="text-xs text-blue-600">Detecting traits to pre-fill character details</p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      ) : (
        /* Face detection UI for multi-character */
        <FaceDetectionWizard
          ref={faceDetectionRef}
          imageUrl={photoUrl!}
          onFacesConfirmed={handleFacesConfirmed}
          onManualMode={handleManualMode}
          form={form}
          photoConsent={photoConsent}
          fromCoverSlug={coverSlug}
        />
      )}
      
      {/* Simplified disclaimer - just a tooltip icon */}
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowDisclaimer(!showDisclaimer)}
          className="text-gray-500 hover:text-gray-700"
        >
          <Info className="w-4 h-4 mr-1" />
          Photo likeness info
        </Button>
      </div>
      
      {/* Expandable disclaimer */}
      <AnimatePresence>
        {showDisclaimer && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <h4 className="font-medium text-amber-800 mb-2">
                About Photo Likeness
              </h4>
              <ul className="space-y-1 text-amber-700">
                <li>• We create our best interpretation</li>
                <li>• Results vary by photo quality</li>
                <li>• Better results with clear, well-lit photos</li>
                <li>• Preview before purchasing</li>
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
